import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertCircle, CreditCard, Shield, Download, Trash2, FileText, Users, Mail, Edit2, UserMinus, Check, Building2, Save, RefreshCcw, ExternalLink, XCircle, Clock, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingData, setExportingData] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Subscription details state
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  
  // Dynamic pricing state
  const [pricingData, setPricingData] = useState(null);
  
  // Invoice settings state
  const [invoiceSettings, setInvoiceSettings] = useState({
    company_name: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    company_logo_url: '',
    tax_label: 'Tax Number',
    tax_number: '',
    bank_name: '',
    branch_identifier: '',
    branch_identifier_label: 'SWIFT/BIC Code',
    bank_address: ''
  });
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Team management state
  const [teamData, setTeamData] = useState({ members: [], free_member_limit: 2, extra_member_price: 3 });
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermissions, setInvitePermissions] = useState({
    dashboard: { enabled: true, can_edit: false },
    sales: { enabled: false, can_edit: false },
    bookings: { enabled: false, can_edit: false },
    expenses: { enabled: false, can_edit: false },
    documents: { enabled: false, can_edit: false },
    archived: { enabled: false, can_edit: false }
  });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editPermissions, setEditPermissions] = useState({
    dashboard: { enabled: false, can_edit: false },
    sales: { enabled: false, can_edit: false },
    bookings: { enabled: false, can_edit: false },
    expenses: { enabled: false, can_edit: false },
    documents: { enabled: false, can_edit: false },
    archived: { enabled: false, can_edit: false }
  });

  useEffect(() => {
    fetchSettings();
    fetchTeamMembers();
    fetchInvoiceSettings();
    fetchSubscriptionDetails();
    fetchPricingData();
    
    // Check for subscription success from URL params
    const subscriptionStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');
    
    if (subscriptionStatus === 'success' && sessionId) {
      // Verify payment status
      verifyPayment(sessionId);
    }
  }, [searchParams]);
  
  const fetchPricingData = async () => {
    try {
      const response = await axios.get(`${API}/admin/public/pricing`);
      setPricingData(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };
  
  const getDisplayPricing = () => {
    if (!pricingData?.regions?.length) {
      return { monthly: '€9.99', yearly: '€99.99' };
    }
    const defaultRegion = pricingData.regions.find(r => r.region === pricingData.default_region) || pricingData.regions[0];
    const currencySymbol = defaultRegion.currency === 'EUR' ? '€' : defaultRegion.currency === 'USD' ? '$' : defaultRegion.currency;
    return {
      monthly: `${currencySymbol}${defaultRegion.monthly_price}`,
      yearly: `${currencySymbol}${defaultRegion.yearly_price}`
    };
  };
  
  const verifyPayment = async (sessionId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.get(`${API}/subscriptions/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('settings.subscriptionActivated') || 'Subscription activated successfully!');
      // Remove query params from URL
      navigate('/app/settings', { replace: true });
      // Refresh data
      fetchSettings();
      fetchSubscriptionDetails();
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  };
  
  const fetchSubscriptionDetails = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/subscriptions/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error(t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/invoices/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoiceSettings(response.data);
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    }
  };

  const handleSaveInvoiceSettings = async () => {
    setSavingInvoiceSettings(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/invoices/settings`, invoiceSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('invoice.settingsSaved'));
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/invoices/settings/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      setInvoiceSettings({...invoiceSettings, company_logo_url: response.data.logo_url});
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setInvoiceSettings({...invoiceSettings, company_logo_url: ''});
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/team/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamData(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleInviteTeamMember = async () => {
    if (!inviteEmail) {
      toast.error(t('common.required'));
      return;
    }
    
    // Check if at least one tab is enabled
    const hasEnabledTab = Object.values(invitePermissions).some(p => p.enabled);
    if (!hasEnabledTab) {
      toast.error(t('team.selectAtLeastOneTab'));
      return;
    }
    
    setSendingInvite(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Convert new permission format to backend format
      const permissionsForBackend = {
        dashboard: invitePermissions.dashboard.enabled,
        dashboard_edit: invitePermissions.dashboard.can_edit,
        sales: invitePermissions.sales.enabled,
        sales_edit: invitePermissions.sales.can_edit,
        bookings: invitePermissions.bookings.enabled,
        bookings_edit: invitePermissions.bookings.can_edit,
        expenses: invitePermissions.expenses.enabled,
        expenses_edit: invitePermissions.expenses.can_edit,
        documents: invitePermissions.documents.enabled,
        documents_edit: invitePermissions.documents.can_edit,
        archived: invitePermissions.archived.enabled,
        archived_edit: invitePermissions.archived.can_edit
      };
      
      await axios.post(
        `${API}/team/invite`,
        { email: inviteEmail, permissions: permissionsForBackend },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('team.invitationSent'));
      setShowInviteDialog(false);
      setInviteEmail('');
      setInvitePermissions({
        dashboard: { enabled: true, can_edit: false },
        sales: { enabled: false, can_edit: false },
        bookings: { enabled: false, can_edit: false },
        expenses: { enabled: false, can_edit: false },
        documents: { enabled: false, can_edit: false },
        archived: { enabled: false, can_edit: false }
      });
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setSendingInvite(false);
    }
  };

  const handleUpdatePermissions = async () => {
    if (!editingMember) return;
    
    // Check if at least one tab is enabled
    const hasEnabledTab = Object.values(editPermissions).some(p => p.enabled);
    if (!hasEnabledTab) {
      toast.error(t('team.selectAtLeastOneTab'));
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Convert new permission format to backend format
      const permissionsForBackend = {
        dashboard: editPermissions.dashboard.enabled,
        dashboard_edit: editPermissions.dashboard.can_edit,
        sales: editPermissions.sales.enabled,
        sales_edit: editPermissions.sales.can_edit,
        bookings: editPermissions.bookings.enabled,
        bookings_edit: editPermissions.bookings.can_edit,
        expenses: editPermissions.expenses.enabled,
        expenses_edit: editPermissions.expenses.can_edit,
        documents: editPermissions.documents.enabled,
        documents_edit: editPermissions.documents.can_edit,
        archived: editPermissions.archived.enabled,
        archived_edit: editPermissions.archived.can_edit
      };
      
      await axios.put(
        `${API}/team/members/${editingMember.invitation_id}`,
        { permissions: permissionsForBackend },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('team.permissionsUpdated'));
      setShowEditDialog(false);
      setEditingMember(null);
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const handleRemoveMember = async (invitationId) => {
    if (!window.confirm(t('team.confirmRemove'))) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/team/members/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('team.memberRemoved'));
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    // Convert backend permission format to frontend UI format
    const perms = member.permissions || {};
    setEditPermissions({
      dashboard: { enabled: perms.dashboard || false, can_edit: perms.dashboard_edit || false },
      sales: { enabled: perms.sales || false, can_edit: perms.sales_edit || false },
      bookings: { enabled: perms.bookings || false, can_edit: perms.bookings_edit || false },
      expenses: { enabled: perms.expenses || false, can_edit: perms.expenses_edit || false },
      documents: { enabled: perms.documents || false, can_edit: perms.documents_edit || false },
      archived: { enabled: perms.archived || false, can_edit: perms.archived_edit || false }
    });
    setShowEditDialog(true);
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('settings.subscriptionCanceled') || 'Subscription will be cancelled at the end of the billing period');
      fetchSettings();
      fetchSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setCancellingSubscription(false);
    }
  };
  
  const handleReactivateSubscription = async () => {
    setReactivatingSubscription(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('settings.subscriptionReactivated') || 'Subscription reactivated');
      fetchSettings();
      fetchSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setReactivatingSubscription(false);
    }
  };
  
  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/subscriptions/portal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleSubscribe = () => {
    window.location.href = '/subscription';
  };

  // GDPR: Export user data
  const handleExportData = async () => {
    setExportingData(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/gdpr/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tourpilot_data_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('gdpr.dataExported'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setExportingData(false);
    }
  };

  // GDPR: Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error(t('gdpr.typeDeleteToConfirm'));
      return;
    }
    
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API}/gdpr/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear all local storage and redirect
      localStorage.clear();
      toast.success(t('gdpr.accountDeleted'));
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setDeletingAccount(false);
      setShowDeleteDialog(false);
    }
  };

  // Get privacy consent info
  const getConsentInfo = () => {
    const consentDate = localStorage.getItem('privacy_consent_date');
    return consentDate ? new Date(consentDate).toLocaleDateString() : null;
  };

  if (loading) {
    return <div className="text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold text-foreground tracking-tight">{t('settings.title')}</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">{t('settings.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading font-semibold">{t('settings.accountInformation')}</CardTitle>
            <CardDescription className="text-sm">{t('settings.yourAccountDetails')}</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('common.name')}</p>
              <p className="text-sm sm:text-base font-medium">{settings?.name}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('common.email')}</p>
              <p className="text-sm sm:text-base font-medium break-all">{settings?.email}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('settings.operatorId')}</p>
              <p className="text-sm sm:text-base font-mono break-all">{settings?.operator_id}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('settings.memberSince')}</p>
              <p className="text-sm sm:text-base">{new Date(settings?.created_at).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading font-semibold flex items-center gap-2">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
              {t('nav.subscription')}
            </CardTitle>
            <CardDescription className="text-sm">{t('settings.yourSubscriptionPlan')}</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-2">{t('common.status')}</p>
                <Badge
                  variant={
                    subscriptionDetails?.status === 'active'
                      ? 'default'
                      : subscriptionDetails?.status === 'canceled'
                      ? 'destructive'
                      : 'secondary'
                  }
                  data-testid="subscription-status-badge"
                >
                  {subscriptionDetails?.status?.toUpperCase() || settings?.subscription_status?.toUpperCase() || 'TRIAL'}
                </Badge>
              </div>
              
              {/* Recurring badge */}
              {subscriptionDetails?.is_recurring && !subscriptionDetails?.cancel_at_period_end && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <RefreshCcw className="w-3 h-3" />
                  {t('settings.recurringPayment') || 'Recurring'}
                </Badge>
              )}
              
              {subscriptionDetails?.cancel_at_period_end && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  {t('settings.cancelPending') || 'Cancels at period end'}
                </Badge>
              )}
            </div>

            {(subscriptionDetails?.plan || settings?.subscription_plan) && (
              <div>
                <p className="text-sm text-muted-foreground">{t('settings.plan')}</p>
                <p className="text-base font-medium capitalize">{subscriptionDetails?.plan || settings?.subscription_plan}</p>
              </div>
            )}

            {(subscriptionDetails?.expires || settings?.subscription_expires) && (
              <div>
                <p className="text-sm text-muted-foreground">
                  {subscriptionDetails?.is_recurring 
                    ? (t('settings.nextBillingDate') || 'Next Billing Date')
                    : t('settings.expires')}
                </p>
                <p className="text-base">
                  {new Date(subscriptionDetails?.expires || settings?.subscription_expires).toLocaleDateString()}
                </p>
              </div>
            )}
            
            {subscriptionDetails?.days_remaining !== null && subscriptionDetails?.days_remaining !== undefined && (
              <div>
                <p className="text-sm text-muted-foreground">{t('settings.daysRemaining') || 'Days Remaining'}</p>
                <p className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  {subscriptionDetails.days_remaining} {t('settings.days') || 'days'}
                </p>
              </div>
            )}
            
            {/* Billing info note */}
            {subscriptionDetails?.is_recurring && !subscriptionDetails?.cancel_at_period_end && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <RefreshCcw className="w-4 h-4 inline mr-2" />
                  {t('settings.recurringInfo') || 'Your subscription will automatically renew. You will be charged at the beginning of each billing period.'}
                </p>
              </div>
            )}

            <div className="pt-4 space-y-2">
              {subscriptionDetails?.status === 'active' || settings?.subscription_status === 'active' ? (
                <>
                  {subscriptionDetails?.cancel_at_period_end ? (
                    <Button
                      variant="default"
                      onClick={handleReactivateSubscription}
                      disabled={reactivatingSubscription}
                      className="w-full"
                    >
                      <RefreshCcw className={`w-4 h-4 mr-2 ${reactivatingSubscription ? 'animate-spin' : ''}`} />
                      {reactivatingSubscription 
                        ? (t('common.loading') || 'Loading...')
                        : (t('settings.reactivateSubscription') || 'Reactivate Subscription')}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          data-testid="cancel-subscription-button"
                          className="w-full"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          {t('settings.cancelSubscription')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('settings.cancelSubscription')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('settings.cancelConfirmation') || 'Your subscription will remain active until the end of the current billing period. You can reactivate anytime before then.'}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={handleCancelSubscription}
                            disabled={cancellingSubscription}
                          >
                            {cancellingSubscription ? (t('common.loading') || 'Loading...') : (t('settings.confirmCancel') || 'Yes, Cancel Subscription')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  
                  {subscriptionDetails?.has_stripe_subscription && (
                    <Button
                      variant="outline"
                      onClick={handleOpenBillingPortal}
                      disabled={openingPortal}
                      className="w-full"
                    >
                      <ExternalLink className={`w-4 h-4 mr-2 ${openingPortal ? 'animate-spin' : ''}`} />
                      {openingPortal 
                        ? (t('common.loading') || 'Loading...')
                        : (t('settings.manageBilling') || 'Manage Billing & Invoices')}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  data-testid="subscribe-button"
                  className="w-full"
                >
                  {t('settings.subscribeNow')}
                </Button>
              )}
            </div>

            {(subscriptionDetails?.status !== 'active' && settings?.subscription_status !== 'active') && (
              <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">{t('settings.noActiveSubscription')}</p>
                  <p>{t('settings.subscribeToAccess')}</p>
                  <p className="mt-2">
                    {t('settings.plans')}: {getDisplayPricing().monthly}/{t('settings.month')} or {getDisplayPricing().yearly}/{t('settings.year')}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Management Section */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading font-semibold flex items-center gap-2">
                <Users className="w-6 h-6" />
                {t('team.title')}
              </CardTitle>
              <CardDescription>{t('team.subtitle')}</CardDescription>
            </div>
            <Button onClick={() => setShowInviteDialog(true)}>
              <Mail className="w-4 h-4 mr-2" />
              {t('team.inviteTeamMember')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pricing Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-muted-foreground">
                {t('team.pricing.freeMembersInfo', { count: teamData.free_member_limit })}
              </span>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">
                {t('team.pricing.extraMemberCost', { price: teamData.extra_member_price })}
              </span>
              {teamData.free_slots_remaining > 0 && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-green-600 font-medium">
                    {t('team.pricing.freeSlots', { count: teamData.free_slots_remaining })}
                  </span>
                </>
              )}
              {teamData.monthly_cost > 0 && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-primary font-medium">
                    {t('team.pricing.currentCost', { cost: teamData.monthly_cost.toFixed(2) })}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Team Members List */}
          {teamData.members.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('team.noMembers')}</p>
              <p className="text-sm">{t('team.noMembersDesc')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">{t('team.currentMembers')}</h4>
              {teamData.members.map((member) => {
                const perms = member.permissions || {};
                return (
                  <div
                    key={member.invitation_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-border rounded-lg gap-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-medium text-foreground">{member.email}</span>
                        <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                          {member.status === 'active' ? t('team.active') : t('team.pending')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {perms.dashboard && (
                          <Badge variant="outline" className={`text-xs ${perms.dashboard_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.dashboard')}: {perms.dashboard_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                        {perms.sales && (
                          <Badge variant="outline" className={`text-xs ${perms.sales_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.sales')}: {perms.sales_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                        {perms.bookings && (
                          <Badge variant="outline" className={`text-xs ${perms.bookings_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.bookings')}: {perms.bookings_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                        {perms.expenses && (
                          <Badge variant="outline" className={`text-xs ${perms.expenses_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.expenses')}: {perms.expenses_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                        {perms.documents && (
                          <Badge variant="outline" className={`text-xs ${perms.documents_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.documents')}: {perms.documents_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                        {perms.archived && (
                          <Badge variant="outline" className={`text-xs ${perms.archived_edit ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-600'}`}>
                            {t('team.tabs.archived')}: {perms.archived_edit ? t('team.edit') : t('team.view')}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {member.joined_at 
                          ? `${t('team.joinedOn')} ${new Date(member.joined_at).toLocaleDateString()}`
                          : `${t('team.invitedOn')} ${new Date(member.invited_at).toLocaleDateString()}`
                        }
                      </div>
                    </div>
                    <div className="flex gap-2 self-end sm:self-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(member)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveMember(member.invitation_id)}
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Settings Section */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading font-semibold flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                {t('invoice.settings')}
              </CardTitle>
              <CardDescription>{t('invoice.settingsDesc')}</CardDescription>
            </div>
            <Button onClick={handleSaveInvoiceSettings} disabled={savingInvoiceSettings}>
              <Save className="w-4 h-4 mr-2" />
              {savingInvoiceSettings ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Company Information */}
          <div>
            <h4 className="font-medium text-foreground mb-4">{t('invoice.companyInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">{t('invoice.companyName')}</Label>
                <Input
                  id="company_name"
                  value={invoiceSettings.company_name}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, company_name: e.target.value})}
                  placeholder={t('invoice.companyNamePlaceholder')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="company_email">{t('invoice.companyEmail')}</Label>
                <Input
                  id="company_email"
                  type="email"
                  value={invoiceSettings.company_email}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, company_email: e.target.value})}
                  placeholder={t('invoice.companyEmailPlaceholder')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="company_phone">{t('invoice.companyPhone')}</Label>
                <Input
                  id="company_phone"
                  value={invoiceSettings.company_phone}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, company_phone: e.target.value})}
                  placeholder={t('invoice.companyPhonePlaceholder')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('invoice.companyLogo')}</Label>
                <div className="mt-1 space-y-3">
                  {/* Logo Preview */}
                  {invoiceSettings.company_logo_url && (
                    <div className="flex items-center gap-4 p-3 border rounded-lg bg-muted/30">
                      <img 
                        src={invoiceSettings.company_logo_url} 
                        alt="Company Logo" 
                        className="h-16 max-w-[200px] object-contain"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleRemoveLogo}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        type="button"
                        disabled={uploadingLogo}
                        asChild
                      >
                        <span>
                          {uploadingLogo ? (
                            <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </span>
                      </Button>
                    </label>
                    <span className="text-xs text-muted-foreground">Max 2MB, PNG/JPG recommended</span>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="company_address">{t('invoice.companyAddress')}</Label>
                <Textarea
                  id="company_address"
                  value={invoiceSettings.company_address}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, company_address: e.target.value})}
                  placeholder={t('invoice.companyAddressPlaceholder')}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Tax Information */}
          <div>
            <h4 className="font-medium text-foreground mb-4">{t('invoice.taxInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tax_label">{t('invoice.taxLabel')}</Label>
                <Input
                  id="tax_label"
                  value={invoiceSettings.tax_label}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, tax_label: e.target.value})}
                  placeholder="Tax Number / VAT ID / GST"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="tax_number">{t('invoice.taxNumber')}</Label>
                <Input
                  id="tax_number"
                  value={invoiceSettings.tax_number}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, tax_number: e.target.value})}
                  placeholder={t('invoice.taxNumberPlaceholder')}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Bank Information */}
          <div>
            <h4 className="font-medium text-foreground mb-4">{t('invoice.bankInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bank_name">{t('invoice.bankName')}</Label>
                <Input
                  id="bank_name"
                  value={invoiceSettings.bank_name}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, bank_name: e.target.value})}
                  placeholder={t('invoice.bankNamePlaceholder')}
                  className="mt-1"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label htmlFor="branch_identifier" className="mb-0">{t('invoice.branchIdentifier')}</Label>
                  <Select
                    value={invoiceSettings.branch_identifier_label}
                    onValueChange={(v) => setInvoiceSettings({...invoiceSettings, branch_identifier_label: v})}
                  >
                    <SelectTrigger className="w-[140px] h-7 text-xs">
                      <SelectValue placeholder="Label type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SWIFT/BIC Code">SWIFT/BIC</SelectItem>
                      <SelectItem value="Branch Code">Branch Code</SelectItem>
                      <SelectItem value="Sort Code">Sort Code</SelectItem>
                      <SelectItem value="IFSC Code">IFSC Code</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  id="branch_identifier"
                  value={invoiceSettings.branch_identifier}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, branch_identifier: e.target.value})}
                  placeholder={t('invoice.branchIdentifierPlaceholder')}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="bank_address">{t('invoice.bankAddress')}</Label>
                <Textarea
                  id="bank_address"
                  value={invoiceSettings.bank_address}
                  onChange={(e) => setInvoiceSettings({...invoiceSettings, bank_address: e.target.value})}
                  placeholder={t('invoice.bankAddressPlaceholder')}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GDPR & Data Privacy Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Shield className="w-6 h-6" />
            {t('gdpr.dataPrivacy')}
          </CardTitle>
          <CardDescription>{t('gdpr.dataPrivacyDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Consent Status */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-foreground">{t('gdpr.consentStatus')}</span>
              <Badge variant="default">{t('gdpr.consentGiven')}</Badge>
            </div>
            {getConsentInfo() && (
              <p className="text-sm text-muted-foreground">
                {t('gdpr.consentDate')}: {getConsentInfo()}
              </p>
            )}
          </div>

          {/* Data Rights Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Export Data */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Download className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{t('gdpr.exportData')}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('gdpr.exportDataDesc')}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleExportData}
                    disabled={exportingData}
                  >
                    {exportingData ? t('common.loading') : t('gdpr.downloadData')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Delete Account */}
            <div className="border border-destructive/30 rounded-lg p-4 bg-destructive/5">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{t('gdpr.deleteAccount')}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('gdpr.deleteAccountDesc')}
                  </p>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    {t('gdpr.deleteMyAccount')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Policy Link */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>{t('gdpr.reviewPolicies')}</span>
            </div>
            <div className="flex gap-4">
              <a href="/privacy-policy" className="text-sm text-primary hover:underline">
                {t('footer.privacyPolicy')}
              </a>
              <a href="/terms-of-service" className="text-sm text-primary hover:underline">
                {t('footer.termsOfService')}
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {t('gdpr.deleteAccountTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('gdpr.deleteAccountWarning')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm">
              <p className="font-medium text-destructive mb-2">{t('gdpr.thisWillDelete')}:</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>{t('gdpr.deleteItem1')}</li>
                <li>{t('gdpr.deleteItem2')}</li>
                <li>{t('gdpr.deleteItem3')}</li>
                <li>{t('gdpr.deleteItem4')}</li>
              </ul>
            </div>
            <div>
              <Label htmlFor="delete-confirm">{t('gdpr.typeDeleteToConfirm')}</Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== 'DELETE' || deletingAccount}
            >
              {deletingAccount ? t('common.loading') : t('gdpr.permanentlyDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Team Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {t('team.inviteTeamMember')}
            </DialogTitle>
            <DialogDescription>
              {t('team.inviteDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="invite-email">{t('team.emailAddress')}</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.enterEmail')}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label className="mb-3 block">{t('team.selectTabsAndPermissions')}</Label>
              <div className="space-y-3 border rounded-lg p-4">
                {['dashboard', 'sales', 'bookings', 'expenses', 'documents', 'archived'].map((tab) => (
                  <div key={tab} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`invite-${tab}`}
                        checked={invitePermissions[tab]?.enabled}
                        onCheckedChange={(checked) => 
                          setInvitePermissions(prev => ({ 
                            ...prev, 
                            [tab]: { ...prev[tab], enabled: checked }
                          }))
                        }
                      />
                      <Label htmlFor={`invite-${tab}`} className="text-sm font-medium">
                        {t(`team.tabs.${tab}`)}
                      </Label>
                    </div>
                    {invitePermissions[tab]?.enabled && (
                      <Select
                        value={invitePermissions[tab]?.can_edit ? 'edit' : 'view'}
                        onValueChange={(value) => 
                          setInvitePermissions(prev => ({ 
                            ...prev, 
                            [tab]: { ...prev[tab], can_edit: value === 'edit' }
                          }))
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">{t('team.viewOnly')}</SelectItem>
                          <SelectItem value="edit">{t('team.canEdit')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleInviteTeamMember} disabled={sendingInvite || !inviteEmail}>
              {sendingInvite ? t('team.sending') : t('team.sendInvitation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              {t('team.editPermissions')}
            </DialogTitle>
            <DialogDescription>
              {editingMember?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="mb-3 block">{t('team.selectTabsAndPermissions')}</Label>
              <div className="space-y-3 border rounded-lg p-4">
                {['dashboard', 'sales', 'bookings', 'expenses', 'documents', 'archived'].map((tab) => (
                  <div key={tab} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-${tab}`}
                        checked={editPermissions[tab]?.enabled || false}
                        onCheckedChange={(checked) => 
                          setEditPermissions(prev => ({ 
                            ...prev, 
                            [tab]: { ...prev[tab], enabled: checked }
                          }))
                        }
                      />
                      <Label htmlFor={`edit-${tab}`} className="text-sm font-medium">
                        {t(`team.tabs.${tab}`)}
                      </Label>
                    </div>
                    {editPermissions[tab]?.enabled && (
                      <Select
                        value={editPermissions[tab]?.can_edit ? 'edit' : 'view'}
                        onValueChange={(value) => 
                          setEditPermissions(prev => ({ 
                            ...prev, 
                            [tab]: { ...prev[tab], can_edit: value === 'edit' }
                          }))
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">{t('team.viewOnly')}</SelectItem>
                          <SelectItem value="edit">{t('team.canEdit')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdatePermissions}>
              {t('team.updatePermissions')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
